import { useEffect, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

import { StackIcon } from '@radix-ui/react-icons';
import { EmptyState, Flex, Select, Tabs, Text } from '@raystack/apsara';
import _ from 'lodash';
import moment from 'moment';
import { Bar, Cell } from 'recharts';

import BarGraph from '@src/components/Custom/BarGraph';
import * as Skeleton from '@src/components/PixxelUIKit/Skeleton';
import { For, Show } from '@src/hoc';

import styles from './styles.module.css';

export const SEASON_POINTS = {
  SOS: 'sos',
  POS: 'pos',
  EOS: 'eos'
};

const seasonOptions = [
  {
    id: SEASON_POINTS.SOS,
    value: SEASON_POINTS.SOS,
    label: (
      <Text weight={400} size={2} className={styles.seasonLabel}>
        SOS <span>(Start of season)</span>
      </Text>
    )
  },
  {
    id: SEASON_POINTS.POS,
    value: SEASON_POINTS.POS,
    label: (
      <Text weight={400} size={2} className={styles.seasonLabel}>
        POS <span>(Peak of season)</span>
      </Text>
    )
  },
  {
    id: SEASON_POINTS.EOS,
    value: SEASON_POINTS.EOS,
    label: (
      <Text weight={400} size={2} className={styles.seasonLabel}>
        EOS <span>(End of season)</span>
      </Text>
    )
  }
];
const DATE_FILTER = {
  WEEK: '1W',
  MONTH: '1M',
  YEAR: '1Y'
};

const toggleGroup = [
  { id: DATE_FILTER.WEEK, value: DATE_FILTER.WEEK },
  { id: DATE_FILTER.MONTH, value: DATE_FILTER.MONTH },
  { id: DATE_FILTER.YEAR, value: DATE_FILTER.YEAR }
];

const getBarColor = val => {
  switch (val) {
    case SEASON_POINTS.SOS:
      return '#ACD8FC';
    case SEASON_POINTS.POS:
      return '#ACDEC8';
    case SEASON_POINTS.EOS:
      return '#CBCDFF';
  }
};
export const getActiveBarColor = val => {
  switch (val) {
    case SEASON_POINTS.SOS:
      return '#0D74CE';
    case SEASON_POINTS.POS:
      return '#29A383';
    case SEASON_POINTS.EOS:
      return '#5B5BD6';
  }
};

const getGraphItemData = ({ date, selectedPoint, farmsValid, label }) => ({
  date,
  label: label,
  farms: farmsValid,
  farmCount: farmsValid.length,
  area: farmsValid.reduce((acc, d) => acc + d.area, 0),
  color: getBarColor(selectedPoint),
  activeColor: getActiveBarColor(selectedPoint)
});

const matchDates = (d1, d2, unit) => d1.isSame(moment(d2), unit);

const getValidFarms = (farms, selectedPoint, date, unit) => {
  return farms
    .map(farm => {
      let cycles = [];
      switch (selectedPoint) {
        case SEASON_POINTS.SOS:
          cycles = farm?.cycles?.filter(d => matchDates(date, d.SOS, unit));
          break;
        case SEASON_POINTS.EOS:
          cycles = farm?.cycles?.filter(d => matchDates(date, d.EOS, unit));
          break;
        case SEASON_POINTS.POS:
          cycles = farm?.cycles?.filter(d =>
            matchDates(date, d['Peak date'], unit)
          );
          break;
      }
      if (cycles?.length > 0) {
        return { ...farm, cycles };
      }
    })
    .filter(d => !!d);
};

const getDataForYearFilter = ({ startDate, endDate, farms, selectedPoint }) => {
  const graphInfo = [];
  const unit = 'month';
  while (startDate.isBefore(endDate) || startDate.isSame(endDate, unit)) {
    const farmsValid = getValidFarms(farms, selectedPoint, startDate, unit);
    graphInfo.push(
      getGraphItemData({
        date: startDate.format('MMM'),
        label: startDate.format('MMMM YYYY'),
        farmsValid,
        selectedPoint
      })
    );
    startDate.add(1, unit);
  }
  return graphInfo;
};

const getDataForMonthFilter = ({ dates, farms, selectedPoint }) => {
  const graphInfo = [];
  const unit = 'day';
  for (let i = 0; i < dates.length - 1; i++) {
    let d1 = moment(dates[i]),
      d2 = moment(dates[i + 1]);

    let farmsValid = [];
    while (d1.isBefore(d2)) {
      farmsValid = [
        ...farmsValid,
        ...getValidFarms(farms, selectedPoint, d1, unit)
      ];
      d1.add(1, unit);
    }

    graphInfo.push(
      getGraphItemData({
        date: moment(dates[i]).format('D MMM'),
        label: `${moment(dates[i]).format('D MMM')} - ${moment(dates[i + 1])
          .subtract(1, unit)
          .format('D MMM YYYY')}`,
        farmsValid,
        selectedPoint
      })
    );
  }

  return graphInfo;
};

const getDataForWeekFilter = ({ startDate, endDate, farms, selectedPoint }) => {
  const graphInfo = [];
  const unit = 'day';
  while (startDate.isBefore(endDate)) {
    const farmsValid = getValidFarms(farms, selectedPoint, startDate, unit);
    graphInfo.push(
      getGraphItemData({
        date: startDate.format('D MMM'),
        label: startDate.format('D MMM YYYY'),
        farmsValid,
        selectedPoint
      })
    );
    startDate.add(1, unit);
  }
  return graphInfo;
};

const CycleGraphTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const {
      payload: { label, area, farmCount }
    } = payload[0];
    const data = [
      { label: 'No. of farms', value: farmCount },
      {
        label: 'Total farm area',
        value: (
          <>
            {area.toFixed(2)} m<sup>2</sup>
          </>
        )
      }
    ];
    return (
      <Flex
        direction='column'
        gap='small'
        align='start'
        className={styles.graphTooltipWrapper}
      >
        <Text size={2} weight={500} variant='primary'>
          {label}
        </Text>
        <For each={data}>
          {(d, i) => (
            <Flex key={i} align='stretch'>
              <Text
                size={2}
                weight={400}
                variant='secondary'
                className={styles.toolTipKey}
              >
                {d.label}
              </Text>
              <Text size={2} weight={400} variant='secondary'>
                : {d.value}
              </Text>
            </Flex>
          )}
        </For>
      </Flex>
    );
  }
};

const CropPhenCycleStatsSection = ({
  vectorData,
  loader,
  focusOnFarm,
  setHighlightedFarms,
  setSeasonMode: setSeasonPoint,
  setHoverId
}) => {
  const [graphData, setGraphData] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(SEASON_POINTS.SOS);
  const [selectedFilter, setSelectedFilter] = useState(DATE_FILTER.YEAR);
  const [activeIndex, setActiveIndex] = useState(null);
  const [tableData, setTableData] = useState([]);
  const ref = useRef(null);

  const handleBarClick = (data, index) => {
    const farms = data?.payload?.farms || [];
    setActiveIndex(index);
    setTableData(farms);
    setHighlightedFarms(farms);
  };

  useEffect(() => {
    if (!_.isEmpty(vectorData)) {
      setActiveIndex(null);
      setSeasonPoint(selectedPoint);
      const cycleData = Object.values(vectorData)?.[0] || {};
      const dates = cycleData.Datetime || [];
      const startDate = moment(cycleData.start_date);
      const endDate = moment(cycleData.end_date);
      const farms = Object.entries(vectorData).map(([key, value]) => ({
        ...value,
        id: key,
        area: Number((value.Area || 0).toFixed(2))
      }));

      let graphInfo = [];

      if (selectedFilter === DATE_FILTER.YEAR) {
        graphInfo = getDataForYearFilter({
          startDate,
          endDate,
          selectedPoint,
          farms
        });
      } else if (selectedFilter === DATE_FILTER.MONTH) {
        graphInfo = getDataForMonthFilter({ selectedPoint, dates, farms });
      } else if (selectedFilter === DATE_FILTER.WEEK) {
        graphInfo = getDataForWeekFilter({
          startDate,
          endDate,
          selectedPoint,
          farms
        });
      }
      graphInfo = graphInfo.filter(d => !!d.farmCount);
      setGraphData(graphInfo);

      const allFarms = Object.values(graphInfo)
        .map(d => d.farms)
        .flat();
      setHighlightedFarms(allFarms);
      setTableData(allFarms);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vectorData, selectedFilter, selectedPoint]);
  return (
    <Show when={!loader} fallback={<Skeleton.TileList />}>
      <Flex justify='between' align='center' style={{ width: '100%' }}>
        <Select
          value={selectedPoint}
          onValueChange={value => setSelectedPoint(value)}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <For each={seasonOptions}>
              {d => (
                <Select.Item value={d.value} key={d.id}>
                  {d.label}
                </Select.Item>
              )}
            </For>
          </Select.Content>
        </Select>

        <Tabs
          value={selectedFilter}
          onValueChange={val => setSelectedFilter(val)}
          className={styles.tabsList}
        >
          <Tabs.List>
            <For each={toggleGroup}>
              {group => (
                <Tabs.Trigger
                  value={group.value}
                  key={group.id}
                  data-test-id={`${group.value}-btn`}
                >
                  {group.value}
                </Tabs.Trigger>
              )}
            </For>
          </Tabs.List>
        </Tabs>
      </Flex>
      <Show
        when={!_.isEmpty(graphData)}
        fallback={
          <EmptyState
            icon={<StackIcon />}
            heading='No farms available'
            subHeading='Please change the season point!'
          />
        }
      >
        <BarGraph
          data={graphData}
          axis={{
            x: { key: 'date' },
            y: {
              key: 'farmCount',
              label: 'No. of Farms',
              tickFormatter: val => val
            }
          }}
          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
          barWidth={40}
          tooltipProps={{ content: <CycleGraphTooltip /> }}
        >
          <Bar
            onClick={handleBarClick}
            dataKey='farmCount'
            data-test-id='crop-phen-bar-click'
            style={{ cursor: 'pointer' }}
          >
            {graphData?.map((bar, index) => (
              <Cell
                key={index}
                fill={index === activeIndex ? bar.activeColor : bar.color}
              />
            ))}
          </Bar>
        </BarGraph>
        <Flex direction='column' style={{ width: '100%', marginBottom: 32 }}>
          <Flex align='center' justify='between'>
            <Text
              className={styles.tableCell}
              size={2}
              weight={500}
              variant='tertiary'
            >
              Farm Name
            </Text>
            <Text
              className={styles.tableCell}
              size={2}
              weight={500}
              variant='tertiary'
            >
              Area
            </Text>
          </Flex>
          <AutoSizer style={{ height: '400px' }}>
            {({ height, width }) => (
              <FixedSizeList
                ref={ref}
                itemData={tableData}
                itemCount={tableData?.length}
                itemSize={50}
                height={height}
                width={width}
              >
                {({ data, index, style }) => (
                  <Flex
                    className={styles.tableItem}
                    style={style}
                    align='center'
                    justify='between'
                    data-test-id={`farm-table-item-${data[index].id}`}
                    onClick={() => focusOnFarm(data[index].id)}
                    onMouseEnter={() => setHoverId(data[index].id)}
                    onMouseLeave={() => setHoverId(null)}
                  >
                    <Text
                      className={styles.tableCell}
                      size={4}
                      weight={400}
                      variant='secondary'
                    >
                      Farm {data[index].id}
                    </Text>
                    <Text
                      className={styles.tableCell}
                      size={4}
                      weight={400}
                      variant='secondary'
                    >
                      {data[index].area} m<sup>2</sup>
                    </Text>
                  </Flex>
                )}
              </FixedSizeList>
            )}
          </AutoSizer>
        </Flex>
      </Show>
    </Show>
  );
};

export default CropPhenCycleStatsSection;
